import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';

export const dataFetchMixin = {

    //********************* GET PARTICIPANT IDS ********************************//
    // The participant ids that identify the active dm/channel, used to match
    // "discussed items" via gh-users-side-bar.getDiscussedItems, and as the
    // participants for a new item-thread (see openItemConversation). Outside
    // a dm/channel (e.g. viewMode 'no_discussion') there's no one but the
    // active user, matching the solo item-thread /gudhub-messenger/item
    // already accepts (empty participants + created_by).
    getParticipantIds() {
        if(this.viewMode === 'channel') return this.channelState.participants;
        if(this.viewMode === 'dm') return [this.activeUserId, this.dmState.otherUserId];
        return [];
    },

    //********************* GET SIDEBAR ********************************//
    // The gh-users-side-bar instance, used to read its "discussed items" data
    // (see getDiscussedItemEntries).
    getSidebar() {
        return this.closest('gudhub-messenger')?.querySelector('gh-users-side-bar');
    },

    //********************* FIND OR CREATE CONVERSATION ********************************//
    // Returns the conversation_id of the existing direct conversation with
    // otherUserId, creating one via POST /create if none exists yet.
    // * otherUserId (string) — GudHub user id of the DM partner
    async findOrCreateConversation(otherUserId) {
        const listResponse = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/list?user_id=${this.activeUserId}`);
        const conversations = await listResponse.json();

        const existing = conversations.find(conversation =>
            conversation.type === 'direct' &&
            conversation.participants.map(String).includes(otherUserId)
        );

        if(existing) {
            return { conversationId: existing.conversation_id, isNew: false };
        }

        const createResponse = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                type: 'direct',
                participants: [otherUserId],
                created_by: this.activeUserId
            })
        });

        const conversation = await createResponse.json();

        return { conversationId: conversation.conversation_id, isNew: true };
    },

    //********************* FETCH CONVERSATION ********************************//
    // Fetches the full conversation document by id (participants, created_at,
    // item_ref, messages, unread_count for the active user).
    // * conversationId (string) — conversation_id to fetch
    async fetchConversation(conversationId) {
        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${conversationId}?user_id=${this.activeUserId}`);
        return await response.json();
    },

    //********************* LOAD MESSAGES ********************************//
    // Fetches the full conversation and returns its messages normalized for
    // rendering (see normalizeMessage).
    // * conversationId (string) — conversation_id to load
    async loadMessages(conversationId) {
        const conversation = await this.fetchConversation(conversationId);

        return conversation.messages.map(message => this.normalizeMessage(message));
    },

    //********************* NORMALIZE MESSAGE ********************************//
    // Converts a raw GudHub Messenger API message into the shape used for
    // rendering: text messages get type=null and pre-rendered markdown,
    // attachment messages get their content unwrapped to a plain URL.
    // * message (Object) — raw message from the GudHub Messenger API
    normalizeMessage(message) {
        const isAttachment = typeof message.content === 'object';
        const c = isAttachment ? message.content : null;

        return {
            message_id: message.message_id,
            timestamp: message.timestamp,
            user_id: String(message.user_id),
            type: message.type === 'text' ? null : message.type,
            content: isAttachment ? c.url : message.content,
            thumbnail_url: c?.thumbnail_url || null,
            file_name: c?.name || null,
            filename: c?.filename || null,
            file_size: c?.size ?? null,
            mime_type: c?.mime_type || null,
            renderedContent: isAttachment ? '' : this.renderMarkdown(message.content),
            thread_id: message.thread_id || null,
            thread_reply_count: message.thread_reply_count || 0,
            thread_participants: (message.thread_participants || []).map(String),
            reactions: (message.reactions || []).map(r => ({ emoji: r.emoji, user_ids: r.user_ids.map(String) }))
        };
    },

    //********************* ACTIVE MESSAGES ********************************//
    // Returns the messages array of the currently active view (dm, channel, or thread).
    activeMessages() {
        if(this.viewMode === 'thread') return this.threadState.messages;
        if(this.viewMode === 'channel') return this.channelState.messages;
        return this.dmState.messages;
    },

    //********************* RESOLVE USER ********************************//
    // Synchronous participant lookup, backed by prefetchParticipants().
    // * userId (string) — GudHub user id
    resolveUser(userId) {
        return this.participantsCache.get(String(userId)) || { fullname: 'Not Found' };
    },

    //********************* PREFETCH PARTICIPANTS ********************************//
    // Resolves any user ids not yet in participantsCache via gudhub.getUserById,
    // so resolveUser() can stay synchronous during rendering.
    // * ids (Array<string|number>) — GudHub user ids to ensure are cached
    async prefetchParticipants(ids) {
        const missing = [...new Set(ids.map(String))].filter(id => !this.participantsCache.has(id));

        if(!missing.length) return;

        const users = await Promise.all(missing.map(id => gudhub.getUserById(id)));

        missing.forEach((id, index) => this.participantsCache.set(id, users[index]));
    },

    //********************* PREFETCH THREAD PARTICIPANTS ********************************//
    // Resolves the users who replied in each message's thread (see
    // normalizeMessage), so renderThreadFooter() can render their avatars
    // synchronously.
    // * messages (Array) — normalized messages (see normalizeMessage)
    async prefetchThreadParticipants(messages) {
        await this.prefetchParticipants(messages.flatMap(message => message.thread_participants));
    },

    //********************* CHECK OTHER USER APP ACCESS ********************************//
    async checkOtherUserAppAccess(appId) {
        this._otherUserHasAppAccess = false;

        if(!this.dmState?.otherUserId || !appId) return;

        const groups = await gudhub.groupSharing.getGroupsByApp(appId);
        if(!groups?.length) return;

        const otherUserId = String(this.dmState.otherUserId);

        const groupUsersLists = await Promise.all(
            groups.map(group => gudhub.groupSharing.getUsersByGroup(group.group_id))
        );

        for(const groupUsers of groupUsersLists) {
            if(groupUsers?.some(user => String(user.user_id) === otherUserId)) {
                this._otherUserHasAppAccess = true;
                return;
            }
        }
    },

    //********************* MARK AS READ ********************************//
    // Advances the active user's read cursor to the active conversation's
    // latest message and tells the sidebar to clear the corresponding unread
    // badge (dm badge only when marking a dm itself as read).
    async markAsRead() {
        await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${this.conversationId}/read`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: this.activeUserId })
        });

        this.dispatchEvent(new CustomEvent('conversation_read', {
            bubbles: true,
            composed: true,
            detail: {
                conversation_id: this.conversationId,
                user_id: this.viewMode === 'dm' ? this.dmState.otherUserId : null
            }
        }));
    },
};
